import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {
  fetchAndActivate,
  getString,
  RemoteConfig,
} from "@angular/fire/remote-config";
import { from, Subject, Observable, of, forkJoin } from "rxjs";
import {
  catchError,
  concatMap,
  finalize,
  map,
  take,
  tap,
} from "rxjs/operators";
import { HttpService } from "./http.service";
import { IDBService } from "./idb.service";
import { environment } from "src/environments/environment";
import { DirectoryStructure } from "src/models/dataset.model";
import { ApiResponse } from "src/models/api-response.model";
import { LangRemoteConfig } from "src/models/lang-remote-config.model";
import { GoogleTranslateResp } from "src/models/google-translate-resp.model";
import {
  TranslationConfig,
  TranslationInference,
} from "src/models/bhashini-api.model";
import { apiPrefix } from "src/constants/api-prefix";
import { ApiEndpoints } from "src/constants/api-endpoints";
import {
  ModelVersion,
  VersionDirectory,
  VersionFile,
} from "src/models/models.model";
import {
  ArtefactStats,
  ArtefactStatsBySector,
  ArtefactUploadCount,
  ArtifactDownload,
  DatasetResponse,
  ExplorerRegistrations,
  PublishStats,
  RoleWiseInsights,
  UserRoleStats,
} from "src/models/dashboard-stats.model";
import { UtilityService } from "./utility.service";
import { ApiHandlerService } from "./api-handler.service";
import { LoaderAppService } from "src/pages/shared/loader/loader.service";
import { ArtefactType } from "src/constants/artefact-types";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private isModelsPipelineFetching = false;
  private readonly modelsPipelineNotify$ = new Subject<any>();

  constructor(
    private http: HttpClient,
    private remoteConfig: RemoteConfig,
    private httpService: HttpService,
    private idbService: IDBService,
    private utilityService: UtilityService,
    private apiHandlerService: ApiHandlerService,
    protected _loaderService: LoaderAppService
  ) {}

  $modelVersion(data: any) {
    return <ModelVersion>data;
  }

  $versionFile(data: any) {
    return <VersionFile>data;
  }

  $versionDirectory(data: any) {
    return <VersionDirectory>data;
  }

  getBannerList(params: any) {
    return this.httpService.getRequest(ApiEndpoints.bannerUrl, params);
  }

  getBanner(params: any) {
    return this.httpService.getRequest(ApiEndpoints.bannerUrl, params);
  }

  getDepartmentList(params: any = [], loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.departmentListUrl,
      params,
      undefined,
      loader
    );
  }

  getServiceList(params?: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.serviceListUrl,
      params,
      undefined,
      loader
    );
  }

  postBanner(bodyObj: any) {
    return this.httpService.postRequest(ApiEndpoints.bannerUrl, bodyObj);
  }

  updateBanner(bannerId: any, bodyObj: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.bannerUrl}?id=${bannerId}`,
      bodyObj
    );
  }

  deleteBanner(bannerId: any) {
    return this.httpService.deleteRequest(
      `${ApiEndpoints.bannerUrl}?id=${bannerId}`
    );
  }

  publishBanner(bannerId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.publishBannerUrl}?id=${bannerId}`,
      {}
    );
  }

  getUserList(params: any, isLoader: boolean = true) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.getUserListUrl}`,
      params,
      undefined,
      undefined,
      isLoader
    );
  }

  assignDOA(payload: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}/organization-assignment/assign`,
      payload
    );
  }

  deleteUserByID(id: any) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.deleteUserUrl}${id}`
    );
  }

  getUserCountApi() {
    return this.httpService.getRequest(
      ApiEndpoints.userCountApiUrl,
      undefined,
      undefined,
      false
    );
  }

  getEventCountApi() {
    return this.httpService.getRequest(
      ApiEndpoints.eventCountApiUrl,
      undefined,
      undefined,
      false
    );
  }

  createUserApi(body: any) {
    return this.httpService.postRequest(ApiEndpoints.userListUrl, body);
  }

  provideEventAccessApi(body: any) {
    return this.httpService.postRequest(
      ApiEndpoints.provideEventAccessUrl,
      body
    );
  }

  getCaptcha(params?: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.captchaUrl}`,
      params
    );
  }

  login(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.loginUrl}`,
      bodyObj
    );
  }

  resetPassword(bodyObj: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.resetPasswordUrl}`,
      bodyObj
    );
  }

  addUserRoleAPI(bodyObj: any) {
    return this.httpService.postRequest(ApiEndpoints.addUserRoleUrl, bodyObj);
  }

  deleteUserRoleAPI(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.deleteUserRoleUrl,
      bodyObj
    );
  }

  deletePermissionAPI(permission_id: any) {
    return this.httpService.deleteRequest(
      `${ApiEndpoints.deletePermissionUrl}?permission_id=${permission_id}`
    );
  }

  fetchUserPermission(userId: any, headerArray: any) {
    return this.httpService.getRequest(
      ApiEndpoints.fetchUserPermissionUrl,
      [{ key: "user_id", value: userId }],
      headerArray
    );
  }

  getUserDetail(params: any) {
    return this.httpService.getRequest(ApiEndpoints.userDetailUrl, params);
  }

  getUserRoles(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.userRolesurl, params);
  }

  getRoles(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.rolesurl, params);
  }

  updateUserDetail(bodyObj: any) {
    return this.httpService.putRequest(ApiEndpoints.updateUserUrl, bodyObj);
  }

  logout() {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.logoutUrl}`
    );
  }

  createSchemaConfig(bodyObj: any) {
    return this.httpService.postRequest(ApiEndpoints.createConfigUrl, bodyObj);
  }

  getfavouriteDepartmentList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.favouriteDepartmentListUrl,
      params,
      undefined,
      loader
    );
  }

  getfavouriteDepartmentCountList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.favouriteDepartmentCountListUrl,
      params,
      undefined,
      loader
    );
  }

  getfavouriteServiceList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.favouriteServiceListUrl,
      params,
      undefined,
      loader
    );
  }

  getfavouriteServiceCountList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.favouriteServiceCountListUrl,
      params,
      undefined,
      loader
    );
  }

  getSchemaConfig(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.createConfigUrl, params);
  }

  getFavoriteSchemeList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.schemeFavoriteListUrl,
      params,
      undefined,
      loader
    );
  }

  getFavoriteSchemeCountList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.schemeFavoriteCountListUrl,
      params,
      undefined,
      loader
    );
  }

  // fetch user activity list
  getUserActivityList(params: any) {
    return this.httpService.getRequest(ApiEndpoints.userActivityList, params);
  }

  // fetch user activity list
  getSystemLogs(params: any) {
    return this.httpService.getRequest(ApiEndpoints.systemLogsList, params);
  }
  // fetch user activity list Count
  getSystemLogsCount(params: any) {
    return this.httpService.getRequest(
      ApiEndpoints.systemLogsListCount,
      params
    );
  }

  // fetch user search list
  getUserSearchActivityList(params: any) {
    return this.httpService.getRequest(
      ApiEndpoints.userSearchActivityList,
      params
    );
  }

  // fetch api hits logs List
  getApiHitsLogsList(params: any) {
    return this.httpService.getRequest(ApiEndpoints.apiHitsLogsList, params);
  }

  // get aviled services
  getAvailedServicesList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.availedServicesList,
      params,
      undefined,
      loader
    );
  }

  addDepartmentTopRated(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.topRatedDepartmentUrl,
      bodyObj
    );
  }

  addDepartmentSuggested(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.suggestedDepartmentUrl,
      bodyObj
    );
  }

  getDepartmentTopRatedList() {
    return this.httpService.getRequest(ApiEndpoints.topRatedDepartmentUrl);
  }

  getDepartmentSuggested(category: string) {
    return this.httpService.getRequest(
      `${ApiEndpoints.departmentsCategoriesUrl}?category=${category}`
    );
  }

  getoccupationList(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.occupationListUrl, params);
  }

  getqualificationList(params?: any) {
    return this.httpService.getRequest(
      ApiEndpoints.qualificationListUrl,
      params
    );
  }

  getStatesList(headers: any) {
    return this.httpService.getRequest(ApiEndpoints.statesListUrl, [], headers);
  }

  addDepartment(bodyObj: object) {
    return this.httpService.postRequest(ApiEndpoints.departmentUrl, bodyObj);
  }

  updateDepartmentVisibility(bodyObj: object) {
    return this.httpService.putRequest(
      ApiEndpoints.updateDepartmentVisibilityUrl,
      bodyObj
    );
  }

  updateDepartmentMetaData(bodyObj: object) {
    return this.httpService.putRequest(
      ApiEndpoints.updateDepartmentMetaDataUrl,
      bodyObj
    );
  }

  getDepartmentById(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.departmentListUrl, params);
  }

  addDepartmentTranslation(bodyObj: object) {
    return this.httpService.putRequest(
      ApiEndpoints.addDepartmentTranslationUrl,
      bodyObj
    );
  }

  getDepartmentNewAndUpdated() {
    return this.httpService.getRequest(ApiEndpoints.newUpdatedDepartmentUrl);
  }

  addDepartmentNewAndUpdated(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.newUpdatedDepartmentUrl,
      bodyObj
    );
  }

  getAllUserDetails(params: any) {
    return this.httpService.getRequest(ApiEndpoints.userAllDetailsUrl, params);
  }

  addNotification(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.addNotificationUrl,
      bodyObj
    );
  }

  addNotificationAll(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.addNotificationAllUrl,
      bodyObj
    );
  }

  updateNotification(bodyObj: any) {
    return this.httpService.putRequest(
      ApiEndpoints.updateNotificationUrl,
      bodyObj
    );
  }

  getAllNotifications(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.allNotificationURL,
      params,
      undefined,
      loader
    );
  }

  postTrendingDept(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.departmentsCategoriesUrl,
      bodyObj
    );
  }
  deleteTrendingDept(param: any) {
    return this.httpService.deleteRequest(
      ApiEndpoints.departmentsCategoriesUrl,
      param
    );
  }
  getTrendingDept(category: string) {
    return this.httpService.getRequest(
      `${ApiEndpoints.departmentsCategoriesUrl}?category=${category}`
    );
  }

  postTrendingService(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.serviceCategoriesUrl,
      bodyObj
    );
  }
  deleteTrendingService(param: any) {
    return this.httpService.deleteRequest(
      ApiEndpoints.serviceCategoriesUrl,
      param
    );
  }
  getTrendingService(category: string) {
    return this.httpService.getRequest(
      `${ApiEndpoints.serviceCategoriesUrl}?category=${category}`
    );
  }

  getSchemeList() {
    return this.httpService.getRequest(ApiEndpoints.schemeListUrl);
  }

  postTrendingScheme(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.schemeCategoriesUrl,
      bodyObj
    );
  }
  deleteTrendingScheme(param: any) {
    return this.httpService.deleteRequest(
      ApiEndpoints.schemeCategoriesUrl,
      param
    );
  }
  getTrendingScheme(category: string) {
    return this.httpService.getRequest(
      `${ApiEndpoints.schemeCategoriesUrl}?category=${category}`
    );
  }
  getUrl(param: any) {
    return this.httpService.getRequest(ApiEndpoints.getEntityUrl, param);
  }

  getTranslationList(params: any = []) {
    return this.httpService.getRequest(ApiEndpoints.translationListUrl, params);
  }

  addTranslations(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.translationListUrl,
      bodyObj
    );
  }
  getPopularService(category: string) {
    return this.httpService.getRequest(
      `${ApiEndpoints.serviceCategoriesUrl}?category=${category}`
    );
  }
  deletePopularService(param: any) {
    return this.httpService.deleteRequest(ApiEndpoints.popularSrvUrl, param);
  }
  postPopularService(bodyObj: any) {
    return this.httpService.postRequest(
      ApiEndpoints.serviceCategoriesUrl,
      bodyObj
    );
  }

  postAboutUs(bodyObj: any) {
    return this.httpService.postRequest(ApiEndpoints.aboutUsUrl, bodyObj);
  }
  getAboutUs() {
    return this.httpService.getRequest(ApiEndpoints.aboutUsUrl);
  }

  duplicateContent(contentId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.duplicateContentUrl}?content_id=${contentId}`,
      {}
    );
  }

  getPaginatedContentApi(params: any) {
    return this.httpService.getRequest(
      ApiEndpoints.paginatedContentUrl,
      params
    );
  }

  getCoreCategoryList(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.corCategoryListUrl, params);
  }

  getCategoryList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.categoryListUrl,
      params,
      undefined,
      loader
    );
  }

  deleteCategory(params: any) {
    return this.httpService.deleteRequest(ApiEndpoints.categoryListUrl, params);
  }

  addCategory(body: any) {
    return this.httpService.postRequest(ApiEndpoints.categoryListUrl, body);
  }

  addQuickService(body: any) {
    return this.httpService.postRequest(ApiEndpoints.quickServiceListUrl, body);
  }

  deleteQuickService(params: any) {
    return this.httpService.deleteRequest(
      ApiEndpoints.quickServiceListUrl,
      params
    );
  }

  getQuickServiceList(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.quickServiceListUrl,
      params,
      undefined,
      loader
    );
  }

  getCmsConfig() {
    return this.httpService.getRequest(ApiEndpoints.cmsConfigurl);
  }

  updateCmsConfig(body: any) {
    return this.httpService.putRequest(ApiEndpoints.cmsConfigurl, body);
  }

  getMostSearchedApi() {
    return this.httpService.getRequest(ApiEndpoints.mostSearchedurl);
  }

  getTrendingApi(loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.trendingUrl,
      undefined,
      undefined,
      loader
    );
  }

  //  to get the list of doctypes on digilocker page
  getDoctypeListApi(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.doctypeUrl,
      params,
      undefined,
      loader
    );
  }

  //  to update the mapping of doctypes on digilocker page
  updateDoctypesApi(body: any, doctype: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.doctypeUrl}?doctype=${doctype}`,
      body
    );
  }

  // update isssuer state mapping
  updateIssuerStateApi(body: any) {
    return this.httpService.putRequest(`${ApiEndpoints.issuerStateUrl}`, body);
  }

  getParentCategoryApi(loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.parentCategoryUrl,
      undefined,
      undefined,
      loader
    );
  }

  getNewlyAddedDocuments(params: any, headers = undefined, isLoader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.newlyAddedUrl,
      params,
      headers,
      isLoader
    );
  }

  getTrendingDocuments(params: any, loader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.trendingDocumentUrl,
      params,
      undefined,
      loader
    );
  }

  updateParentCategoryApi(body: any, categoryId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.parentCategoryUrl}?category_id=${categoryId}`,
      body
    );
  }

  deleteParentCategoryApi(categoryId: any) {
    return this.httpService.deleteRequest(
      `${ApiEndpoints.parentCategoryUrl}?category_id=${categoryId}`
    );
  }

  addParentCategoryApi(body: any) {
    return this.httpService.postRequest(ApiEndpoints.parentCategoryUrl, body);
  }

  getNotificationReadCount(param: any) {
    return this.httpService.getRequest(
      ApiEndpoints.notificationReadCountUrl,
      param
    );
  }

  syncDepartments(body: any) {
    return this.httpService.postRequest(ApiEndpoints.syncDepartmenUrl, body);
  }

  syncServices(body: any) {
    return this.httpService.postRequest(ApiEndpoints.syncServicesUrl, body);
  }

  syncSchemes(body: any) {
    return this.httpService.postRequest(ApiEndpoints.syncSchemeUrl, body);
  }

  syncElasticIndex(body: any) {
    return this.httpService.postRequest(ApiEndpoints.elasticSyncUrl, body);
  }

  syncDocuments(params?: any) {
    return this.httpService.getRequest(ApiEndpoints.syncDocumentUrl, params);
  }

  getLastSyncDetails(params = undefined, headers = undefined, isLoader = true) {
    return this.httpService.getRequest(
      ApiEndpoints.lastSyncUrl,
      params,
      headers,
      isLoader
    );
  }

  enableQuickService(params: any, body: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.quickServiceListUrl}?category_id=${params.category_id}&service_id=${params.service_id}`,
      body
    );
  }

  enableCategory(params: any, body: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.categoryListUrl}?category_id=${params.category_id}`,
      body
    );
  }

  // get count of services per department
  getServiceCountPerDepartmentApi(department_id: any, loader = true) {
    return this.httpService.getRequest(
      `${ApiEndpoints.serviceOfDepartmentCountUrl}?department_id=${department_id}`,
      undefined,
      undefined,
      loader
    );
  }

  getTotalEntitiesCount(params: any) {
    return this.httpService.getRequest(
      ApiEndpoints.getTotalEntitiesCountUrl,
      params,
      undefined,
      false
    );
  }

  getNewlyAddedDepartmentsApi(param: any, headerArry: any, isToken: boolean) {
    return this.httpService.getRequest(
      ApiEndpoints.getNewlyAddedDepartmentUrl,
      param,
      headerArry,
      isToken
    );
  }

  getNewlyAddedServicesApi(param: any, headerArry: any, isToken: boolean) {
    return this.httpService.getRequest(
      ApiEndpoints.getNewlyAddedServiceUrl,
      param,
      headerArry,
      isToken
    );
  }

  getRolesList(param?: any, headerArry?: any) {
    return this.httpService.getRequest(
      ApiEndpoints.rolesListUrl,
      param,
      headerArry
    );
  }

  getRolesDetails(roleId: number, param?: any, headerArry?: any) {
    return this.httpService.getRequest(
      `${ApiEndpoints.roleDetailsUrl}?role_id=${roleId}`,
      param,
      headerArry
    );
  }

  getPermissionsList(param?: any, headerArry?: any) {
    return this.httpService.getRequest(
      ApiEndpoints.permissionsListUrl,
      param,
      headerArry
    );
  }

  addPermission(body: any) {
    return this.httpService.postRequest(ApiEndpoints.addPermissionUrl, body);
  }

  addRole(body: any) {
    return this.httpService.postRequest(ApiEndpoints.addRoleUrl, body);
  }

  editRole(body: any) {
    return this.httpService.putRequest(ApiEndpoints.editRoleUrl, body);
  }

  getIssuerList(param?: any, headerArry?: any) {
    let url = ApiEndpoints.issuerListUrl;
    return this.httpService.getRequest(url, param, headerArry);
  }

  getHallList() {
    return this.httpService.getRequest(ApiEndpoints.hallListUrl);
  }

  getEventHallLayoutList(params: any) {
    return this.httpService.getRequest(
      ApiEndpoints.eventHallLayoutListUrl,
      params
    );
  }

  getEventList() {
    return this.httpService.getRequest(ApiEndpoints.eventListUrl);
  }

  addEvent(body: any) {
    return this.httpService.postRequest(ApiEndpoints.eventListUrl, body);
  }

  addHallEvent(body: any) {
    return this.httpService.postRequest(
      ApiEndpoints.eventHallLayoutListUrl,
      body
    );
  }

  editEvent(body: any, eventId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.eventListUrl}?eventId=${eventId}`,
      body
    );
  }

  getEventDetails(params: any) {
    return this.httpService.getRequest(ApiEndpoints.eventDetailsUrl, params);
  }

  editHallEvent(body: any, eventHallLayoutId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.eventHallLayoutListUrl}?eventHallLayoutId=${eventHallLayoutId}`,
      body
    );
  }

  deleteHallEvent(eventHallLayoutId: any) {
    return this.httpService.deleteRequest(
      `${ApiEndpoints.eventHallLayoutListUrl}?eventHallLayoutId=${eventHallLayoutId}`
    );
  }

  deleteEvent(eventId: any) {
    return this.httpService.deleteRequest(
      `${ApiEndpoints.eventListUrl}?eventId=${eventId}`
    );
  }

  getStallList(params: any) {
    return this.httpService.getRequest(ApiEndpoints.stallListUrl, params);
  }

  hideDatasets(id: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/inactive/${id}`
    );
  }

  hideModels(id: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/inactive/${id}`
    );
  }

  hideUsecases(id: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}/inactive/${id}`
    );
  }

  exportStallList(params: any) {
    return this.httpService.getRequest(ApiEndpoints.exportStallListUrl, params);
  }

  importStallsAPI(body: any) {
    return this.httpService.postRequest(ApiEndpoints.importStallsUrl, body);
  }

  getPastEventList() {
    return this.httpService.getRequest(ApiEndpoints.pastEventListUrl);
  }

  duplicateEventApi(eventId: any) {
    return this.httpService.putRequest(
      `${ApiEndpoints.duplicateEventUrl}?eventId=${eventId}`,
      {}
    );
  }

  // event visit API
  eventVisitAPI(params: any = []) {
    return this.httpService.getRequest(ApiEndpoints.eventVisitUrl, params);
  }

  // hall visit API
  hallVisitAPI(params: any = []) {
    return this.httpService.getRequest(ApiEndpoints.hallVisitUrl, params);
  }

  // stall visit API
  stallVisitAPI(params: any = []) {
    return this.httpService.getRequest(ApiEndpoints.stallVisitUrl, params);
  }

  // top search keywords API
  topSearchKeywordsAPI(params: any = []) {
    return this.httpService.getRequest(ApiEndpoints.searchKeywordsUrl, params);
  }

  // upload file server on API
  uploadFileApi(bodyObj: any, isLoading: boolean = false) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.fileUploadUrl}`,
      bodyObj,
      undefined,
      undefined,
      isLoading
    );
  }

  private getBhashiniModelsPipeline(targetLang: string, sourceLang = "en") {
    const cacheKey = "bhashini_models_pipeline";
    const requestBody = {
      pipelineTasks: [
        {
          taskType: "translation",
        },
      ],
      pipelineRequestConfig: {
        pipelineId: environment.bhashini_api_creds.pipeline_id,
      },
    };

    let modelsPipeline$: Observable<any>;

    if (this.isModelsPipelineFetching) {
      modelsPipeline$ = this.modelsPipelineNotify$.pipe(take(1));
    } else {
      this.isModelsPipelineFetching = true;

      modelsPipeline$ = from(
        this.idbService.fetchData<{ timeout: number; data: any } | undefined>(
          cacheKey
        )
      ).pipe(
        concatMap((cachedData) => {
          if ((cachedData?.timeout || 0) > new Date().getTime()) {
            return of(cachedData!.data);
          }

          return this.http
            .post<any>(ApiEndpoints.bhashiniModelsPipelineUrl, requestBody, {
              headers: new HttpHeaders({
                ulcaApiKey: environment.bhashini_api_creds.ulca_api_key,
                userID: environment.bhashini_api_creds.user_id,
              }),
            })
            .pipe(
              tap((resp) => {
                const timeout = new Date().getTime() + 24 * 60 * 60 * 1000; // 24hrs
                this.idbService
                  .storeData(cacheKey, { timeout, data: resp })
                  .catch(console.log);
              })
            );
        }),
        tap({
          next: (resp) => {
            this.modelsPipelineNotify$.next(resp);
          },
        }),
        finalize(() => {
          this.isModelsPipelineFetching = false;
        })
      );
    }

    return modelsPipeline$.pipe(
      map((resp) => {
        const translationConfig: TranslationConfig | undefined =
          resp.pipelineResponseConfig.find(
            ({ taskType }: any) => taskType === "translation"
          );

        if (!translationConfig) {
          throw new Error("No translation config found!");
        }

        const serviceId = translationConfig.config.find(
          ({ language: { sourceLanguage, targetLanguage } }) =>
            sourceLanguage === sourceLang && targetLanguage === targetLang
        )?.serviceId;

        if (!serviceId) {
          throw new Error(`No model ID found for ${targetLang}`);
        }

        return serviceId;
      })
    );
  }

  private getBhashiniInferencePipeline(
    serviceId: string,
    targetLang: string,
    texts: string[],
    sourceLang = "en"
  ) {
    const requestBody = {
      pipelineTasks: [
        {
          taskType: "translation",
          config: {
            language: {
              sourceLanguage: sourceLang,
              targetLanguage: targetLang,
            },
            serviceId,
          },
        },
      ],
      inputData: {
        input: texts.map((text) => ({ source: text })),
      },
    };

    return this.http
      .post<any>(ApiEndpoints.bhashiniInferencePipelineUrl, requestBody, {
        headers: {
          Authorization: environment.bhashini_api_creds.authorization_token,
        },
      })
      .pipe(
        map((resp) => {
          const translationResp: TranslationInference | undefined =
            resp.pipelineResponse.find(
              ({ taskType }: any) => taskType === "translation"
            );

          if (!translationResp) {
            throw new Error("No translations found!");
          }

          return translationResp.output.map(({ target }) => target);
        })
      );
  }

  getTextTranslations(
    targetLang: string,
    texts: string[],
    sourceLang?: string
  ) {
    texts = texts.filter((text) => !!text);

    return from(fetchAndActivate(this.remoteConfig)).pipe(
      concatMap(() => {
        const configJSON = getString(
          this.remoteConfig,
          environment.lang_remote_config
        );

        const parsedConfig = <LangRemoteConfig>JSON.parse(configJSON);

        const { source } = parsedConfig.find(
          ({ value }) => value === targetLang
        )!;

        if (source === "google") {
          const translateBody = {
            q: texts,
            target: targetLang,
          };

          return this.http
            .post<GoogleTranslateResp>(
              ApiEndpoints.googleTranslateUrl,
              translateBody,
              {
                params: new HttpParams().set(
                  "key",
                  environment.google_translate_key
                ),
              }
            )
            .pipe(
              map(({ data }) =>
                data.translations.map(({ translatedText }) => translatedText)
              )
            );
        }

        return this.getBhashiniModelsPipeline(targetLang, sourceLang).pipe(
          concatMap((serviceId) =>
            this.getBhashiniInferencePipeline(
              serviceId,
              targetLang,
              texts,
              sourceLang
            )
          )
        );
      })
    );
  }

  updateStall(stallId: number, updateStallBody: any) {
    return this.httpService.putRequest(
      `/v1/admin/stall?stallId=${stallId}`,
      updateStallBody
    );
  }

  /**
   *
   * @param dataSetBody
   * @returns
   */
  createDataSet(dataSetBody: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}`,
      dataSetBody
    );
  }

  createDataSetMetadata(id: string, dataSetBody: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/metadata`,
      dataSetBody
    );
  }

  importDatasetFiles(files: any) {
    return this.httpService.postRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}${ApiEndpoints.datasetImportUrl}`,
      files
    );
  }

  getDatasetById(id: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/detail`,
      undefined,
      undefined,
      true
    );
  }

  updateMetadataById(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.updateDatasetMetadataUrl}/${id}`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }

  deleteDatasetById(id: any) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/delete`
    );
  }

  updateDatasetById(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/update`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }

  createArtefactThroughRedirectionUrl(data: any, artefactType: string) {
    switch (artefactType) {
      case "models":
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/${artefactType}/redirect`,
          data
        );
      case "datasets":
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/redirect`,
          data
        );
      default:
        throw new Error("Invalid artefact type");
    }
  }

  redirectMetadata(data: any, artefactType: string) {
    switch (artefactType) {
      case "datasets":
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/redirect/metadata`,
          data
        );
      case "models":
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/${artefactType}/redirect/metadata`,
          data
        );
      default:
        throw new Error("Invalid artefact type");
    }
  }

  getDataSetList(params: any, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}`,
      params,
      undefined,
      isLoader
    );
  }

  getDataSetListWeb(params: any, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}/v1/datasets`,
      params,
      undefined,
      isLoader
    );
  }

  getDataSetListBySearch(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/search`,
      params,
      undefined,
      true
    );
  }

  getAccessTypeList() {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}${ApiEndpoints.datasetAccessTypesUrl}`,
      undefined,
      undefined,
      true
    );
  }

  // uploadFileApiWithLoader(id: string, files: FormData) {
  //   return this.httpService.postRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.fileDetailsUrl}${id}/file-details`,
  //     files,
  //     undefined,
  //     undefined,
  //     true
  //   );
  // }

  getDatasetVersionListWithPagination(
    datasetId: string,
    page: number,
    size = 10,
    userId?: string,
    isLoader = true
  ) {
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrlV1}/${datasetId}/versions`,
        [
          { key: "page", value: page },
          { key: "size", value: size },
        ],
        undefined,
        isLoader
      )
      .pipe(
        map((resp) => {
          if (resp.header?.success && resp.data) {
            return resp.data.versions;
          }
          if (resp.versions) {
            return resp.versions;
          }
          throw new Error(
            resp.header?.msg || "Failed to fetch dataset versions"
          );
        })
      );
  }

  // getDatasetVersionFilesList(
  //   versionID: string,
  //   datasetId: string,
  //   params: any
  // ) {
  //   return this.httpService.getRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrl}/${datasetId}/version-files/${versionID}`,
  //     params,
  //     undefined,
  //     true
  //   );
  // }

  // getDatasetVersionFileDetails(id: string, fileID: string) {
  //   return this.httpService.getRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrl}/${id}/version-file-detail/${fileID}`,
  //     undefined,
  //     undefined,
  //     true
  //   );
  // }

  getDatasetVersionFileRowData(id: string, fileID: string) {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}${ApiEndpoints.datasetUserVersionUrl}/${id}/version-file-data/${fileID}`,
      undefined,
      undefined,
      true
    );
  }

  // downloadDatasetVersionFile(datasetId: string, versionId: string) {
  //   return this.httpService.getRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.downloadDatasetVersion}/${datasetId}/version/${versionId}`,
  //     undefined,
  //     undefined,
  //     true
  //   );
  // }

  /**
   * Content APIs
   */
  postContentApi(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.contentUrl}`,
      body
    );
  }

  editContentApi(body: any, contentId: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.contentUrl}/${contentId}`,
      body
    );
  }

  getContentApi(params: any) {
    return this.httpService.getRequest(ApiEndpoints.contentUrl, params);
  }

  getAdminContentApi(name: string, versionID: string = "") {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${
        ApiEndpoints.getAdminContentUrl
      }/${name}${versionID ? `/${versionID}` : ""}`
    );
  }

  fetchArticleData(id: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/content/open/detail/${id}`
    );
  }

  getAdminContentVersionsApi(name: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.getAdminContentUrl}/${name}/versions`
    );
  }

  activeAdminContentVersionApi(name: string, version: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.activateContentUrl}${name}/${version}`
    );
  }

  deleteContentApi(contentId: any) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.contentUrl}/${contentId}`
    );
  }

  getArticleList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/article/admin/list`,
      params,
    );
  }

  updateArticleById(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/article/admin/publish/${id}`,
      bodyObj,
    );
  }

  getArticleCategoryList() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/article/admin/category/list`,
      [],
    );
  }

  associateArticleCategory(body: { articleId: string; categorySlug: string }) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/article/admin/associate-category`,
      body,
    );
  }

  fetchArticleDetail(articleId: string) {
    this._loaderService.showLoader();
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}/v1/article/detail/${articleId}`
      )
      .pipe(
        map((resp) => {
          if (resp?.header?.success && resp.data) {
            return resp.data.data;
          }

          throw new Error(resp?.header?.msg);
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  /**
   * UseCase APIs
   */
  getToolkitList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}`,
      params
    );
  }

  createToolkit(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}`,
      bodyObj
    );
  }

  createToolkitSteps(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/add-step`,
      bodyObj
    );
  }

  getToolkitStepById(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/step/${id}`
    );
  }

  updateToolkitStep(id: string, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/step/${id}`,
      bodyObj
    );
  }

  deleteToolkitStepByID(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/step/${id}`
    );
  }

  updateToolkitById(bodyObj: any, id: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/${id}`,
      bodyObj
    );
  }

  getToolkitByID(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/${id}`
    );
  }

  deleteToolkitByID(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/${id}`
    );
  }

  mapToolkitResources(bodyObj: any, id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/${id}/resources`,
      bodyObj
    );
  }

  mapToolkitUsecase(bodyObj: { relationIds: [] }, id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/${id}/toolkits`,
      bodyObj
    );
  }

  addToolkitLinks(bodyObj: any, id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.toolkitUrl}/${id}/links`,
      bodyObj
    );
  }

  rejectToolkit(bodyObj: any, id: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.toolkitSingularUrl}/publish/${id}`,
      bodyObj
    );
  }

  getUseCaseList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}`,
      params
    );
  }

  scrollIntoViewWithOffset(element: HTMLElement, offset = 120) {
    const offsetTop =
      element.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  }

  getModelsById(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}/id/${id}`
    );
  }

  downloadModel(modelId: string, versionId?: string) {
    const params = [{ key: "versionId", value: versionId || "" }];

    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v2/model/download/${modelId}/version`,
      versionId ? params : undefined
    );
  }

  fetchtModelVersions(modelId: string, page: number, size = 10) {
    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}/${modelId}/versions?page=${page}&size=${size}`
    );
  }

  fetchModelFilesByVersions(
    modelId: string,
    versionId?: string,
    parentPath?: string,
    page = 1
  ) {
    const bodyObj = {
      folderPath: parentPath,
      versionId,
      page,
      limit: 10,
    };
    return this.httpService
      .postRequest(
        `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrlv2}/model/file-details/${modelId}`,
        bodyObj
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return {
              ...resp.data,
              directories: resp.data.directories.map((data: any) => {
                return {
                  ...data,
                  isExpanded: false,
                  isFilesLoading: false,
                };
              }),
            };
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  getModelsByCreator(creatorId: string, modelId: string) {
    const params = [{ key: "modelId", value: modelId }];
    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}/models/creator/${creatorId}`,
      params
    );
  }

  fetchRelatedDatasetById(page: number, datasetArray: String[], size = 20) {
    const datasetIdParam = encodeURIComponent(JSON.stringify(datasetArray));

    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}${ApiEndpoints.datasetBaseUrl}/relatedDatasets?page=${page}&size=${size}&datasetId=${datasetIdParam}`
    );
  }

  fetchRelatedModelsById(page: number, modelArray: string[], size = 12) {
    const modelIdParam = encodeURIComponent(JSON.stringify(modelArray));

    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}/relatedModels?page=${page}&size=${size}&modelId=${modelIdParam}`
    );
  }

  fetchModelsForFilters(page: number, filterParams?: HttpParams, size = 12) {
    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}?page=${page}&size=${size}`
    );
  }

  getUseCaseByID(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/id/${id}`
    );
  }

  createUseCase(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}`,
      bodyObj
    );
  }

  updateUseCaseById(bodyObj: any, id: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}/${id}`,
      bodyObj
    );
  }

  deleteUseCaseById(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}/delete/usecase/${id}`
    );
  }

  changeUseCaseOwner(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesAdminUrl}/change/owner`,
      body
    );
  }

  mapModelToUseCase(bodyObj: any, id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/models/${id}`,
      bodyObj
    );
  }

  mapDatasetToUseCase(bodyObj: any, id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/datasets/${id}`,
      bodyObj
    );
  }

  createToUseCaseDetails(id: string, bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesDetailsAdminUrl}`,
      {
        id,
        body: bodyObj,
      }
    );
  }

  updateToUseCaseDetails(bodyObj: any, id: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesDetailsAdminUrl}/${id}`,
      bodyObj
    );
  }

  getModelToUseCase(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/models/${id}`
    );
  }

  getDatasetToUseCase(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/datasets/${id}`
    );
  }

  createUseCaseResources(id: string, body: { link: string; title: string }) {
    return this.httpService.postRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/resources/${id}`,
      body
    );
  }

  deleteUseCaseResources(id: string, body: { relationIds: string[] }) {
    return this.httpService.deleteRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}/resources/${id}`,
      body
    );
  }

  getResourcesList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.resourcesUrl}`,
      params
    );
  }

  // not wokring
  createResourcesLink(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.resourceLinksAdminUrl}`,
      bodyObj,
    );
  }

  deleteResourcesLink(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.resourceLinkDeleteAdminUrl}/${id}`
    );
  }

  deleteToolkitLink(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.toolkitAdminUrl}/${id}`
    );
  }

  getModelList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}`,
      params,
      undefined,
      true
    );
  }

  getModelListWeb(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}/v1/models`,
      params,
      undefined,
      true
    );
  }

  getModelSearch(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/search`,
      params,
      undefined,
      true
    );
  }

  getModelById(id: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}/id/${id}`,
      undefined,
      undefined,
      true
    );
  }
  createModel(dataSetBody: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListUrl}`,
      dataSetBody
    );
  }

  updateModelById(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/${id}`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }
  deleteModelById(id: any) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/${id}`
    );
  }

  createModelMetadata(dataSetBody: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelMetadataAdminUrl}`,
      dataSetBody
    );
  }

  updateModelMetadataById(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelMetadataAdminUrl}/${id}`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }

  getModelVersionList(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/${id}/version`,
      undefined,
      undefined,
      true
    );
  }

  uploadModelFileApiWithLoader(id: string, files: FormData) {
    return this.httpService.postRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}${ApiEndpoints.uploadModelUrl}/${id}/files`,
      files,
      undefined,
      undefined,
      true
    );
  }

  getResourcesByID(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.resourcesUrl}/id/${id}`
    );
  }

  deleteResourcesById(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.resourcesUrl}/${id}`
    );
  }

  createResources(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.resourcesAdminUrl}`,
      bodyObj
    );
  }

  updateResourcesById(bodyObj: any, id: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.resourcesUrl}/${id}`,
      bodyObj
    );
  }

  getUsecaseListWeb(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.USECASE_SERVICE_PREFIX}${ApiEndpoints.usecasesUrl}`,
      params
    );
  }

  // Approval APIs
  getApprovalList(params: any = [], isLoading = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userOrgListUrl}/all`,
      params,
      undefined,
      isLoading
    );
  }

  updateApprovalOrgById(bodyObj: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userOrgListUrl}/action`,
      bodyObj
    );
  }

  // User APIs
  getProfile() {
    return this.httpService.getRequest(
      `${apiPrefix.USER_SERVICE_PREFIX}${ApiEndpoints.getUserProfileUrl}`
    );
  }

  blockUnblockUserAccessList(
    bodyObj: any = {},
    userId: string,
    type: "block" | "unblock" = "block"
  ) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userListUrl}/${userId}/${type}`,
      bodyObj
    );
  }

  getBlockUserReason(userID: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userListUrl}/${userID}/block/details`
    );
  }

  getOrgList() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.getOrgListUrl}`
    );
  }

  getOrgById(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.getOrgByIdUrl}${id}`
    );
  }

  createOrg(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.createOrgUrl}`,
      body
    );
  }

  updateOrg(id: string, body: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.updateOrgUrl}/${id}`,
      body
    );
  }

  addOrgUser(orgId: string, body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.addOrgUserUrl}/${orgId}`,
      body
    );
  }

  getAllOrgList(params: any = []) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.getAllOrgUrl}`,
      params
    );
  }

  getDashboardStats() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_ARTIFACT_SERVICE_PREFIX}${ApiEndpoints.dashboardStatsUrl}`
    );
  }

  getUserStats() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_ARTIFACT_SERVICE_PREFIX}${ApiEndpoints.userStatsUrl}?timePeriod=1_year`
    );
  }

  markDatasetFlag(bodyObj: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.datasetFlag}`,
      bodyObj
    );
  }

  getOrgUserById(id: string, params: any = []) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.getOrgByIdUrl}${id}/users`,
      params
    );
  }

  // Maintenance
  getMaintenance() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.getMaintenanceUrl}`
    );
  }

  updateMaintenance(bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.getMaintenanceUrl}`,
      bodyObj
    );
  }

  createMaintenance(bodyObj: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.getMaintenanceUrl}/create`,
      bodyObj
    );
  }

  addDatasetTheme(bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.bulkThemeUpdate}`,
      bodyObj
    );
  }

  fetchDatasetByUseCaseId(useCaseId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}/v1/datasets?useCaseId=${useCaseId}`
    );
  }

  fetchModelsByUseCaseId(useCaseId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.MODEL_SERVICE_PREFIX}/v1/models?useCaseId=${useCaseId}`
    );
  }

  getDownloadLogs(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_ARTIFACT_SERVICE_PREFIX}${ApiEndpoints.datasetDownloadLogsUrl}`,
      params
    );
  }

  // publishDataset(id: string, bodyObj: any) {
  //   return this.httpService.patchRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.publishDatasetUrl}${id}`,
  //     bodyObj
  //   );
  // }

  publishModel(id: string, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.publishModelsUrl}${id}`,
      bodyObj
    );
  }
  publishUseCase(id: string, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.publishUsecase}${id}`,
      bodyObj
    );
  }

  manageAccessDataset(bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${bodyObj.artifactId}/add-permission`,
      bodyObj
    );
  }

  manageAccessModel(bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.manageAccessModal}`,
      bodyObj
    );
  }

  getPendingRequestCount(): Observable<ApiResponse<PublishStats>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.pendingRequestsCountUrl}`
    );
  }

  getUsersOverview(): Observable<ApiResponse<UserRoleStats>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.usersOverviewUrl}`
    );
  }

  getArtifactCount(): Observable<ApiResponse<ArtefactStats>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.artifactCountUrl}`
    );
  }

  getSectorWiseArtifactCount(): Observable<ApiResponse<ArtefactStatsBySector>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.sectorWiseArtifactCountUrl}`
    );
  }

  getArtifactAccessCount() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.artifactAccessCountUrl}`
    );
  }

  getDatasetTypeCount(): Observable<ApiResponse<DatasetResponse>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.datasetTypeCountUrl}`
    );
  }

  getRegistrationStats(): Observable<ApiResponse<any>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_CONTENT_SERVICE_PREFIX}${ApiEndpoints.registraionCountUrl}`
    );
  }

  getArtefactUpload(
    params: any
  ): Observable<ApiResponse<ArtefactUploadCount[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.uploadCountUrl}`,
      params
    );
  }

  updateOrgDiscoverability(params: any, orgId: string) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.getOrgByIdUrl}${orgId}`,
      undefined,
      params
    );
  }

  fetchDatasetAIScore(datasetId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}/v1/dataset/${datasetId}/version/score`
    );
  }

  fetchDatasetAIScorePDF(
    datasetId: string,
    params?: HttpParams
  ): Observable<Blob> {
    return this.httpService.getRequest(
      `${apiPrefix.DATSET_SERVICE_PREFIX}/v1/dataset/${datasetId}/version/generate-pdf`,
      [
        {
          key: params?.get("key") || "",
          value: params?.get("value") || "",
        },
      ],
      [
        {
          key: "Accept",
          value: "application/pdf",
        },
      ],
      false,
      true
    );
  }

  fetchDatasetVersions(
    datasetId: string,
    page: number,
    size = 10,
    id?: string
  ) {
    return this.httpService
      .getRequest(
        `${apiPrefix.DATSET_SERVICE_PREFIX}/v1/dataset/${datasetId}/versions`,
        [
          {
            key: "page",
            value: page,
          },
          {
            key: "size",
            value: size,
          },
          {
            key: "userId",
            value: id as string,
          },
          {
            key: "datasetId",
            value: datasetId,
          },
        ]
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data.versions;
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  fetchFilesByVersions(
    datasetId: string,
    versionId?: string,
    parentPath?: string | null,
    page = 1
  ): Observable<DirectoryStructure> {
    return this.httpService
      .postRequest(
        `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUploadUrl}/${datasetId}/file-details`,
        {
          folderPath: parentPath,
          versionId,
          page,
          limit: 10,
        }
      )
      .pipe(
        map((resp: ApiResponse<DirectoryStructure>) => {
          if (resp.header.success && resp.data) {
            return {
              ...resp.data,
              directories: resp.data.directories.map((data) => {
                return {
                  ...data,
                  isExpanded: false,
                  isFilesLoading: false,
                };
              }),
            };
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  fetchDatasetVersionFileSummary(datasetId: string, filePath: string) {
    return this.httpService
      .getRequest(
        `${apiPrefix.SELFCARE_DATASET_UPLOAD_PREFIX}/v1/dataset-upload/summarize-file-data/${datasetId}`,
        [
          {
            key: "filePath",
            value: filePath,
          },
        ],
        undefined,
        false,
        false,
        environment.production ? environment.selfcare_url : ""
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  downloadDataset(datasetId: string, versionId: string) {
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrlV1}/${datasetId}/download/${versionId}`,
        undefined,
        undefined,
        false,
        false,
        environment.production ? environment.selfcare_url : ""
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data;
          }
          throw new Error(resp.header.msg);
        })
      );
  }

  downloadDatasetFile(datasetId: string, versionId: string, filePath: string) {
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrlV1}/${datasetId}/download/${versionId}/file`,
        [
          {
            key: "filePath",
            value: filePath,
          },
        ],
        undefined,
        true,
        false,
        environment.production ? environment.selfcare_url : ""
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  postReply(
    ticketNumber: string,
    requestBody: any
    // userId: number,
    // message: string,
    // email: string,
    // status_id?: number
  ) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/create-reply/${ticketNumber}`,
      requestBody
      // { message, email, status_id }
    );
  }

  getChatThread(ticketNumber: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/track-grievance/${ticketNumber}`
    );
  }

  getDisputedGrevList(params: any, email: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/disputed-artefacts-ticket`,
      params,
      undefined,
      true
    );
  }

  getGeneralGrevList(params: any, email: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/raised-grievance-tickets`,
      params,
      undefined,
      true
    );
  }

  getDisputedGriveanceDetails(id: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/track-grievance/${id}`,
      undefined,
      undefined,
      true
    );
  }

  markAtr(ticketNumber: string, bodyObj?: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/${ticketNumber}/mark-atr-requested`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }

  getChatStatus() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/ticket-status`,
      undefined,
      undefined,
      true
    );
  }

  getUsageInsights(params: any): Observable<any[]> {
    const usageInsights = this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.insightUrl}`,
        params
      )
      .pipe(catchError(() => of(null)));

    const userVisits = this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.userVisitsUrl}`,
        params
      )
      .pipe(catchError(() => of(null)));

    return forkJoin([usageInsights, userVisits]);
  }

  getRoleWiseNoOfLogins(
    params: any
  ): Observable<ApiResponse<RoleWiseInsights[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.roleWiseNoOfLoginsUrl}`,
      params
    );
  }

  getRegistrations(
    params: any
  ): Observable<ApiResponse<ExplorerRegistrations[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.registrationUrl}`,
      params
    );
  }

  getAccountDeletion(params: any): Observable<ApiResponse<RoleWiseInsights[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.accountDeletionsUrl}`,
      params
    );
  }

  getArtefactDownloads(
    params: any
  ): Observable<ApiResponse<ArtifactDownload[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.artefactDownloadsUrl}`,
      params
    );
  }

  getContributorUprades(
    params: any
  ): Observable<ApiResponse<ArtifactDownload[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.contributorUpgradesUrl}`,
      params
    );
  }

  getToolKitTryouts(params: any): Observable<ApiResponse<ArtifactDownload[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.toolkitTryoutsUrl}`,
      params
    );
  }

  getNotebookOpened(params: any): Observable<ApiResponse<ArtifactDownload[]>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.notebookOpenedUrl}`,
      params
    );
  }

  getUserDetailss(userId: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}/v1/user-verify/${userId}`
    );
  }

  confirmUserPassword(userId: any, payload: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}/v1/user-verify/${userId}`,
      payload
    );
  }

  getApproverDetails(id: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/approver-details`
    );
  }

  // Permissions
  getPermissionList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionListUrl}`,
      params
    );
  }

  getRolePermissionList() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/find-all-roles`
    );
  }

  createPermission(bodyObj: { resourceId: number; actionId: number }) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/add-permission`,
      bodyObj
    );
  }

  editPermission(bodyObj: {
    resourceId: number;
    actionId: number;
    id: number;
  }) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/update-permission`,
      bodyObj
    );
  }

  createRolePermission(bodyObj: { roleId: number; permissionId: number }) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/assign-permission-to-role`,
      bodyObj
    );
  }

  getPermissionByPermissionID(permissionID: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/find-permission-by-id/${permissionID}`
    );
  }

  editRolePermission(
    pathParam: { roleId: string; permissionId: string },
    bodyObj: { roleId: number; permissionId: number }
  ) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/update-role-permission/${pathParam.roleId}/${pathParam.permissionId}`,
      bodyObj
    );
  }

  deleteRolePermission(permissionId: string, roleId: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/delete-role-permission/${roleId}/${permissionId}`
    );
  }

  deletePermission(permissionId: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.permissionUrl}/delete-permission/${permissionId}`
    );
  }

  addUserRole(body: any): Observable<ApiResponse<any>> {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userRoleAddUrl}`,
      body
    );
  }

  changeUserRole(body: any): Observable<ApiResponse<any>> {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.changeRoleUrl}`,
      body
    );
  }

  viewUserDetail(id: string): Observable<ApiResponse<any>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.viewUserRoleUrl}/${id}`
    );
  }

  // verify(id: any) {
  //   return this.httpService.getRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.userVerifyUrl}/${id}`,
  //   );
  // }

  inActiveUser(id: string): Observable<ApiResponse<any>> {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.inActiveUserUrl}/${id}`
    );
  }

  checkUsernameAndEmailExist(
    valueToCheck: string,
    isExistType: "email" | "username"
  ): Observable<ApiResponse<boolean>> {
    const requiredParams =
      isExistType === "username"
        ? { username: valueToCheck }
        : { email: valueToCheck };
    return this.httpService
      .postRequest(
        `${apiPrefix.USER_SERVICE_PREFIX}${ApiEndpoints.checkUserExist}`,
        requiredParams
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data != null) {
            return resp.data;
          }
          throw new Error(resp.header.msg);
        })
      );
  }

  getUserVisit(params: any): Observable<ApiResponse<any>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.userVisitsUrl}`,
      params
    );
  }

  getUserVisitStats(params: any): Observable<ApiResponse<any>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SEARCH_SERVICE_PREFIX}${ApiEndpoints.userVisitStatsUrl}`,
      params
    );
  }

  getGrevCount() {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_OSTICKET_SERVICE_PREFIX}/v1/osticket/ticket-count/1`
    );
  }

  getManageRolesList(params: any): Observable<ApiResponse<any>> {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}${ApiEndpoints.userRoleManagementListUrl}`,
      params
    );
  }

  // updateDatasetVersionStatus(artefactID: string, versionId: string, body: any) {
  //   return this.httpService.putRequest(
  //     `${apiPrefix.ADMIN_SERVICE_PREFIX}${ApiEndpoints.datasetListUrl}/${artefactID}/version/${versionId}`,
  //     body
  //   );
  // }

  updateModelVersionStatus(artefactID: string, versionId: string, body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/${artefactID}/version/${versionId}`,
      body
    );
  }

  createNewTagsByName(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_CONFIG_SERVICE_PREFIX}/v1/master/tags`,
      body
    );
  }

  getVersionRequestDatast(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionListUrlV1}/list`,
      params
    );
  }

  getVersionRequestModel(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/version-listing`,
      params
    );
  }

  getFlagReason(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/flag-detail`
    );
  }

  importRedirectDatasets(file: File) {
    const extIndex = file.name.lastIndexOf(".");
    const extension = extIndex !== -1 ? file.name.substring(extIndex) : "";
    const baseName =
      extIndex !== -1 ? file.name.substring(0, extIndex) : file.name;

    const sanitizedBaseName = baseName
      .replace(/[.]/g, "")
      .replace(/[^a-zA-Z0-9 _()\-.]/g, "")
      .replace(/\s+/g, "_");

    const sanitizedFileName = `${sanitizedBaseName}${extension}`;

    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type,
    });

    const formData = new FormData();
    formData.append("file", sanitizedFile);

    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/import-redirect`,
      formData
    );
  }

  bulkUpdateDatasets(file: File) {
    const extIndex = file.name.lastIndexOf(".");
    const extension = extIndex !== -1 ? file.name.substring(extIndex) : "";
    const baseName =
      extIndex !== -1 ? file.name.substring(0, extIndex) : file.name;

    const sanitizedBaseName = baseName
      .replace(/[.]/g, "")
      .replace(/[^a-zA-Z0-9 _()\-.]/g, "")
      .replace(/\s+/g, "_");

    const sanitizedFileName = `${sanitizedBaseName}${extension}`;

    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type,
    });

    const formData = new FormData();
    formData.append("file", sanitizedFile);

    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/datasets/update-bulk`,
      formData
    );
  }

  exportOrgList(quey: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}/v1/organization/users/excel/list/org`,
      quey,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        { key: "Content-Type", value: "application/json" },
      ],
      true,
      true
    );
  }

  fetchContributorLeaderboard(page: number, size = 10, filters: any = {}) {
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );
    filteredValues = {
      ...filteredValues,
      page,
      size,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get<
        ApiResponse<any>
      >(`${environment.base_url}/${apiPrefix.USER_SERVICE_PREFIX}/v1/user/contributor/leaderboard?${params}`)
      .pipe(
        map((resp) => {
          if (resp.header.success) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        }),
        finalize(() => {})
      );
  }

  fetchOrganizationLeaderboard(page: number, size = 10, filters: any = {}) {
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );
    filteredValues = {
      ...filteredValues,
      page,
      size,
    };
    const params = this.apiHandlerService.generateParams(filteredValues);
    return this.http
      .get<
        ApiResponse<any>
      >(`${environment.base_url}/${apiPrefix.USER_SERVICE_PREFIX}/v1/organisation/leaderboard/list?${params}`)
      .pipe(
        map((resp) => {
          if (resp.header.success) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        }),
        finalize(() => {})
      );
  }

  fetchSectorsLeaderboard(page: number, size = 10, filters: any = {}) {
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );
    filteredValues = {
      ...filteredValues,
      page,
      size,
    };
    const params = this.apiHandlerService.generateParams(filteredValues);
    return this.http
      .get<ApiResponse<any>>(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/sectors/all?${params}`
      )
      .pipe(
        map((resp) => {
          if (resp.header.success) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        }),
        finalize(() => {})
      );
  }

  fetchArtefactsLeaderboard(page: number, size = 10, filters: any = {}) {
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );
    filteredValues = {
      ...filteredValues,
      page,
      size,
    };
    const params = this.apiHandlerService.generateParams(filteredValues);
    return this.http
      .get<ApiResponse<any>>(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/artefacts/all?${params}`
      )
      .pipe(
        map((resp) => {
          if (resp.header.success) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        }),
        finalize(() => {})
      );
  }

  fetchMembersActivityLeaderboard(page: number, size = 10, filters: any = {}) {
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );
    filteredValues = {
      ...filteredValues,
      page,
      size,
    };
    const params = this.apiHandlerService.generateParams(filteredValues);
    return this.http
      .get<ApiResponse<any>>(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/members/all?${params}`
      )
      .pipe(
        map((resp) => {
          if (resp.header.success) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        }),
        finalize(() => {})
      );
  }

  exportContributorLeaderboardList(filters: any = {}) {
    this._loaderService.showLoader();
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );

    filteredValues = {
      ...filteredValues,
      page: 1,
      size: 100,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get(
        `${environment.base_url}/${apiPrefix.USER_SERVICE_PREFIX}/v1/user/contributor/leaderboard/report?${params}`,
        {
          observe: "response",
          responseType: "blob",
          headers: new HttpHeaders({
            accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        }
      )
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp.body;
          }

          throw new Error("Failed to export contributor leaderboard");
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  exportOrganizationLeaderboardList(filters: any = {}) {
    this._loaderService.showLoader();
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );

    filteredValues = {
      ...filteredValues,
      page: 1,
      size: 100,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get(
        `${environment.base_url}/${apiPrefix.USER_SERVICE_PREFIX}/v1/organisation/leaderboard/report?${params}`,
        {
          observe: "response",
          responseType: "blob",
          headers: new HttpHeaders({
            accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        }
      )
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp.body;
          }

          throw new Error("Failed to export contributor leaderboard");
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  exportArtefactsLeaderboardList(filters: any = {}) {
    this._loaderService.showLoader();
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );

    filteredValues = {
      ...filteredValues,
      page: 1,
      size: 100,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/artefacts/report?${params}`,
        {
          observe: "response",
          responseType: "blob",
          headers: new HttpHeaders({
            accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        }
      )
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp.body;
          }

          throw new Error("Failed to export contributor leaderboard");
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  exportSectorsLeaderboardList(filters: any = {}) {
    this._loaderService.showLoader();
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );

    filteredValues = {
      ...filteredValues,
      page: 1,
      size: 100,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/sectors/report?${params}`,
        {
          observe: "response",
          responseType: "blob",
          headers: new HttpHeaders({
            accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        }
      )
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp.body;
          }

          throw new Error("Failed to export contributor leaderboard");
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  exportMembersactivityLeaderboardList(filters: any = {}) {
    this._loaderService.showLoader();
    let filteredValues = this.utilityService.removeBlankFields<any, any>(
      filters
    );

    filteredValues = {
      ...filteredValues,
      page: 1,
      size: 100,
    };

    const params = this.apiHandlerService.generateParams(filteredValues);

    return this.http
      .get(
        `${environment.base_url}/${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/leaderboard/members/report?${params}`,
        {
          observe: "response",
          responseType: "blob",
          headers: new HttpHeaders({
            accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        }
      )
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp.body;
          }

          throw new Error("Failed to export contributor leaderboard");
        }),
        finalize(() => {
          this._loaderService.hideLoader();
        })
      );
  }

  exportArtefactList(artefactType: string) {
    const artfactName = artefactType === "useCase" ? "usecase" : artefactType;
    let endPoint = "";
    switch (artefactType) {
      case "datasets":
        endPoint = `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/${artfactName}/excel`;
        break;
      case "models":
        endPoint = `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/${artfactName}/excel`;
        break;
      case "useCase":
        endPoint = `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/${artfactName}/excel`;
        break;
      default:
        throw new Error("Invalid artefact type");
    }

    return this.httpService.getRequest(
      `${endPoint}`,
      undefined,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        { key: "Content-Type", value: "application/json" },
      ],
      true,
      true,
    );
  }

  getComments(page: number = 1, size: number = 10, artefactId: string) {
    const filteredValues = [
      { key: "page", value: page },
      { key: "size", value: size },
    ];
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/discussions/${artefactId}`,
        filteredValues
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  getReplyList(
    page: number = 1,
    size: number = 10,
    artefactId: string,
    commentId: number
  ) {
    const filteredValues = [
      { key: "page", value: page },
      { key: "size", value: size },
    ];
    return this.httpService
      .getRequest(
        `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/discussions/replies/${artefactId}/${commentId}`,
        filteredValues
      )
      .pipe(
        map((resp) => {
          if (resp.header.success && resp.data) {
            return resp.data;
          }

          throw new Error(resp.header.msg);
        })
      );
  }

  getSharedUserList(
    artefactId: string,
    artefactType: ArtefactType,
    params: any
  ) {
    switch (artefactType) {
      case "model":
        return this.httpService
          .getRequest(
            `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/shared-artifact-user-list/${artefactId}`,
            params
          )
          .pipe(
            map((resp) => {
              if (resp.header.success && resp.data) {
                return resp.data;
              }

              throw new Error(resp.header.msg);
            })
          );
      case "dataset":
        return this.httpService
          .getRequest(
            `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${artefactId}/shared-artifact-user-list`,
            params
          )
          .pipe(
            map((resp) => {
              if (resp.header.success && resp.data) {
                return resp.data;
              }

              throw new Error(resp.header.msg);
            })
          );
      default:
        throw new Error("Invalid artefact type");
    }
  }

  blockUserList(artefactId: string, artefactType: ArtefactType, params: any) {
    switch (artefactType) {
      case "model":
        return this.httpService
          .getRequest(
            `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/shared-artifact-blocked-user/${artefactId}`,
            params
          )
          .pipe(
            map((resp) => {
              if (resp.header.success && resp.data) {
                return resp.data;
              }

              throw new Error(resp.header.msg);
            })
          );
      case "dataset":
        return this.httpService
          .getRequest(
            `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${artefactId}/shared-artifact-blocked-user`,
            params
          )
          .pipe(
            map((resp) => {
              if (resp.header.success && resp.data) {
                return resp.data;
              }

              throw new Error(resp.header.msg);
            })
          );
      default:
        throw new Error("Invalid artefact type");
    }
  }

  // task api doa
  getOrganizationAssignment(approverId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_USER_SERVICE_PREFIX}/organization-assignment/list/${approverId}`
    );
  }

  getNotebookRequestDetails(id: string, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookRequestDetailsUrl}/${id}`,
      [],
      undefined,
      isLoader
    );
  }

  getNotebookRequests(params: any, isLoader = false) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookRequestsUrl}`,
      params,
      undefined,
      isLoader
    );
  }

  updateNotebookRequestStatus(body: any, isLoader = true) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookRequestStatusUpdateUrl}`,
      body,
      undefined,
      undefined,
      isLoader
    );
  }

  getNotebookRequestHistory(userId: string, params: any, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookRequestHistoryUrl}/${userId}`,
      params,
      undefined,
      isLoader
    );
  }

  addSftpToDataset(datasetId: string, body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrlV1}/${datasetId}/sftp`,
      body,
      undefined,
      undefined,
      false
    );
  }

  datasetSftpFileTransfer(datasetId: string, filePath: string) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUploadUrl}/${datasetId}/transfer-files`,
      { filePath },
      undefined,
      undefined,
      false
    );
  }

  getDatasetSftpTransactions(datasetId: string, type: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUploadUrl}/${datasetId}/transaction/artifact`,
      [
        {
          key: "type",
          value: type,
        },
      ],
      undefined,
      // false
    );
  }

  processFiles(datasetId: string, body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUploadUrl}/${datasetId}/process-files`,
      body
    );
  }

  publishDatasetSFTP(id: string, body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/publish/user-behalf`,
      body
    );
  }

  // user profile
  getUserProfileData(userId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/users/profile/${userId}`
    );
  }
  getUserProfileStats(userId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/users/profile/stats/${userId}`
    );
  }
  getUserProfileStatsLeaderBoard(userId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/users/profile/leaderboard/stats`,
      [{ key: "userId", value: userId }]
    );
  }

  approveDatasetByDoa(id: any, bodyObj: any) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${id}/publish`,
      bodyObj,
      undefined,
      undefined,
      true
    );
  }

  approveDatasetVersionByDoa(artefactID: string, versionId: string, body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetVersionUrlV1}/${artefactID}/id/${versionId}`,
      body
    );
  }

  getCompetitionList(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.COMPETITION_SERVICE_PREFIX}/v1/competition`,
      params
    );
  }

  getAllCompetitions(competitionId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/competition/getAllCompetition`,
      [
        {
          key: "competitionId",
          value: competitionId,
        },
      ]
    );
  }

  updateCompetitionRequestStatus(body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/competition/update-request/status`,
      body
    );
  }

  getAllCompetitionSubmissions(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/competition/get-all-submissions`,
      params
    );
  }

  // Notebook Feedback
  getNotebookFeedback(params: any, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookFeedbackUrl}`,
      params,
      undefined,
      isLoader
    );
  }

  getNotebookForRequest(artefactID: string, isLoader = true) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.notebookFeedbackForRequestUrl}/${artefactID}`,
      undefined,
      undefined,
      isLoader
    );
  }

  getDownloadRequest(
    artifactId: string,
    artefactType: string,
    page = 1,
    size = 10
  ) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_ARTIFACT_SERVICE_PREFIX}/v1/artifacts/download-requests`,
      [
        { key: "artifactId", value: artifactId },
        { key: "artefactType", value: artefactType },
        { key: "page", value: page },
        { key: "size", value: size },
      ]
    );
  }

  downloadRequestRevokeAccess(bodyObj: any, requestId: string) {
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/datasets/artefact/request/${requestId}`,
      bodyObj
    );
  }

  getRequestHistory(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/contributer/history`,
      params
    );
  }

  updateTrendingWeightage(body: any, artefactType: ArtefactType) {
    let endpoint = "";
    if (artefactType === "dataset") {
      endpoint = `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetTrendingWeightageUrl}`;
    } else if (artefactType === "model") {
      endpoint = `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelTrendingWeightageUrl}`;
    } else if (artefactType === "usecase") {
      endpoint = `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}${ApiEndpoints.usecaseTrendingWeightageUrl}`;
    } else {
      throw new Error("Invalid artefact type for trending weightage update");
    }

    return this.httpService.putRequest(`${endpoint}`, body);
  }

  //  Notes Artefact APIs
  createNoteArtefact(
    artefact: ArtefactType,
    body: {
      id: string;
      url?: string;
      description?: string;
      notes?: string;
      dataset?: string;
    }
  ) {
    switch (artefact) {
      case ArtefactType.dataset:
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${body.dataset}/note`,
          body
        );
      case ArtefactType.model:
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/create-model-notes`,
          body
        );

      case ArtefactType.usecase:
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/usecase/create-usecase-notes`,
          body
        );
      case ArtefactType.toolkit:
        return this.httpService.postRequest(
          `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/admin/toolkit/create-toolkit-notes`,
          body
        );
      default:
        throw new Error("Invalid artefact type for creating notes");
    }
  }

  getNotesListArtefact(
    artefact: ArtefactType,
    artefactId: string,
    params?: any
  ) {
    switch (artefact) {
      case ArtefactType.dataset:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetUrlV1}/${artefactId}/notes`,
          params
        );
      case ArtefactType.model:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/model-notes/${artefactId}`,
          params
        );

      case ArtefactType.usecase:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/usecase/usecase-notes/${artefactId}`,
          params
        );
      case ArtefactType.toolkit:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/admin/toolkit/toolkit-notes/${artefactId}`,
          params
        );
      default:
        throw new Error("Invalid artefact type for creating notes");
    }
  }

  getNoteByIdArtefact(artefact: ArtefactType, noteId: string) {
    switch (artefact) {
      case ArtefactType.dataset:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/note/${noteId}`
        );
      case ArtefactType.model:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/model-note/${noteId}`
        );

      case ArtefactType.usecase:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/usecase/usecase-note/${noteId}`
        );
      case ArtefactType.toolkit:
        return this.httpService.getRequest(
          `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/admin/toolkit/toolkit-note/${noteId}`
        );
      default:
        throw new Error("Invalid artefact type for creating notes");
    }
  }

  deleteNoteByIdArtefact(artefact: ArtefactType, noteId: string) {
    switch (artefact) {
      case ArtefactType.dataset:
        return this.httpService.deleteRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/note/${noteId}`
        );
      case ArtefactType.model:
        return this.httpService.deleteRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/model-note/${noteId}`
        );
      case ArtefactType.usecase:
        return this.httpService.deleteRequest(
          `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/usecase/usecase-note/${noteId}`
        );
      case ArtefactType.toolkit:
        return this.httpService.deleteRequest(
          `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/admin/toolkit/toolkit-note/${noteId}`
        );
      default:
        throw new Error("Invalid artefact type for creating notes");
    }
  }

  updateNoteByIdArtefact(
    artefact: ArtefactType,
    noteId: string,
    params: { url?: string[]; description?: string; notes?: string }
  ) {
    switch (artefact) {
      case ArtefactType.dataset:
        return this.httpService.putRequest(
          `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/note/${noteId}`,
          params
        );
      case ArtefactType.model:
        return this.httpService.putRequest(
          `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}/v1/models/model-note/${noteId}`,
          params
        );

      case ArtefactType.usecase:
        return this.httpService.putRequest(
          `${apiPrefix.ADMIN_USECASE_SERVICE_PREFIX}/v1/usecase/usecase-note/${noteId}`,
          params
        );
      case ArtefactType.toolkit:
        return this.httpService.putRequest(
          `${apiPrefix.ADMIN_TOOLKIT_SERVICE_PREFIX}/v1/admin/toolkit/toolkit-note/${noteId}`,
          params
        );
      default:
        throw new Error("Invalid artefact type for creating notes");
    }
  }

  checkDatasetURLAvailability(url: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/check-url`,
      [
        {
          key: "url",
          value: url,
        },
      ]
    );
  }

  exportSubmissions(competitionId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_SERVICE_PREFIX}/v1/admin/competition/submissions/export/${competitionId}`,
      undefined,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        { key: "Content-Type", value: "application/json" },
      ],
      true,
      true
    );
  }

  getImportMasterData(params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMaster}/list`,
      params
    );
  }

  publishImportMaster(id: string) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMasterId}/${id}/publish`,
      {}
    );
  }

  findAllImportDatasets(id: string, params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMasterId}/${id}/imports`,
      params
    );
  }

  deleteImportDataset(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importDatasetId}/${id}/delete`
    );
  }

  updateImportDataset(id: string, body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importDatasetId}/${id}/update`,
      body
    );
  }

  publishImportedDataset(id: string, body: any) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importDatasetId}/${id}/publish`,
      body
    );
  }

  createDraft(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMaster}/create`,
      body
    );
  }

  importDataset(body: any) {
    return this.httpService.postRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importDatasetV1}/create`,
      body
    );
  }

  getDatasetDetailApi(id: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importDatasetId}/${id}/detail`
    );
  }

  deleteMasterByIdApi(id: string) {
    return this.httpService.deleteRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMasterId}/${id}/delete`
    );
  }

  getDatasetStatusCountApi(fileId: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMasterId}/${fileId}/status-count`
    );
  }

  publishAllDatasetApi(fileId: string) {
    return this.httpService.putRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.importMasterId}/${fileId}/status`
    );
  }

  exportNotebookRequests(instanceType: string) {
    return this.httpService.getRequest(
      `${apiPrefix.ADMIN_COMPUTE_PREFIX}${ApiEndpoints.exportNotebookRequests}/${instanceType}`,
      undefined,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      ],
      true,
      true
    );
  }

  getAuditLogs(id: string, params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.AUDIT_SERVICE}/v1/audit-trail/${id}`,
      params,
      undefined,
      false
    );
  }

  getAuditHistory(id: string, params: any) {
    return this.httpService.getRequest(
      `${apiPrefix.AUDIT_SERVICE}/v1/audit-trail/status/history/${id}`,
      params,
      undefined,
      false
    );
  }
  
  getAllEmailTemplates(query: any) {
    return this.httpService.getRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates`,
      query
    );
  }

  createEmailTemplate(requestBody:any){
    return this.httpService.postRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates`,
      requestBody
    );
  }

  getEmailTemplate(emailTemplateId:string){
    return this.httpService.getRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates/${emailTemplateId}`,
    );
  }
  
  deleteEmailTemplate(emailTemplateId:string){
    return this.httpService.deleteRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates/${emailTemplateId}`,
    );
  }

  sendEmailTemplate(requestBody:any){
    return this.httpService.postRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/send-email`,
      requestBody
    );
  }

  createEmailTemplateAsDraft(requestBody:any){
    return this.httpService.postRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates/draft`,
      requestBody
    );
  }

  updateEmailTemplate(emailTemplateId:string, requestBody:any){
    return this.httpService.putRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/templates/${emailTemplateId}`,
      requestBody
    );
  }

  getEmailogs(query: any) {
    return this.httpService.getRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/logs`,
      query
    );
  }

  downloadRecipients(campaignId: number, format: string) {
    return this.httpService.getRequest(
      `${apiPrefix.NOTIFICATION_SERVICE_PREFIX}/v1/promotional-emails/${campaignId}/recipients/download?format=${format}`,
      [],
      [],
      true,  // loader
      true   // isFile
    );
  }
  
  bulkDatasetApprove(ids:string[]) {
    this._loaderService.showLoader();
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_DATASET_SERVICE_PREFIX}${ApiEndpoints.datasetListUrlV1}/bulk-approve`,
      {ids:ids}
    ).pipe(
      finalize(()=>{
        this._loaderService.hideLoader()
      })
    )
  }

  bulkModelApprove(ids: string[]) {
    this._loaderService.showLoader();
    return this.httpService.patchRequest(
      `${apiPrefix.ADMIN_MODEL_SERVICE_PREFIX}${ApiEndpoints.modelListAdminUrl}/bulk-approve`,
      { modelIds: ids }
    ).pipe(
      finalize(()=>{
        this._loaderService.hideLoader()
      })
    )
  }

  getFeedbackList(params?: any) {
    return this.httpService.getRequest(
      `${apiPrefix.FEEDBACK_SERVICE_PREFIX}/v1/feedback`,
      params
    );
  }

  exportFeedbackList(params?: any) {
    return this.httpService.getRequest(
      `${apiPrefix.FEEDBACK_SERVICE_PREFIX}/v1/feedback/export`,
      params,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      ],
      true,
      true
    );
  }

  getIdeaList(params?: any) {
    return this.httpService.getRequest(
      `${apiPrefix.FEEDBACK_SERVICE_PREFIX}/v1/idea`,
      params
    );
  }

  exportIdeaList(params?: any) {
    return this.httpService.getRequest(
      `${apiPrefix.FEEDBACK_SERVICE_PREFIX}/v1/idea/export`,
      params,
      [
        {
          key: "accept",
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      ],
      true,
      true
    );
  }
}
